import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Textarea } from "../components/ui/Textarea";

import { ArrowLeft, Save, Upload, Loader2, Image as ImageIcon, X } from "lucide-react";
import { influencerService, type Influencer } from "../services/influencers";
import { toast } from "sonner";

interface InfluencerFormProps {
  onBack: () => void;
}

export function InfluencerForm({ onBack }: InfluencerFormProps) {
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [formData, setFormData] = useState<Partial<Influencer>>({
    name: "",
    slug: "",
    description: "",
    summary: "",
    image: "",
    cover: "",
    meta_title: "",
    meta_description: "",
    active: true,
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing) {
      fetchInfluencer();
    }
  }, [id]);

  const fetchInfluencer = async () => {
    try {
      setFetching(true);
      const response = await influencerService.getInfluencerById(id!);
      const data = response.data || response;
      setFormData({
        name: data.name || "",
        slug: data.slug || "",
        description: data.description || "",
        summary: data.summary || "",
        image: data.image || "",
        cover: data.cover || "",
        meta_title: data.meta_title || "",
        meta_description: data.meta_description || "",
        active: data.active !== undefined ? data.active : true,
      });
    } catch {
      toast.error("Erro ao carregar influencer");
      onBack();
    } finally {
      setFetching(false);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (value: string) => {
    setFormData((prev) => {
      const shouldUpdateSlug = !prev.slug || prev.slug === generateSlug(prev.name || "");
      return {
        ...prev,
        name: value,
        slug: shouldUpdateSlug ? generateSlug(value) : prev.slug,
      };
    });
  };

  const handleImageUpload = async (field: "image" | "cover", file: File) => {
    try {
      setUploadingImage(field);
      const response = await influencerService.uploadImage(file, "influencers");
      setFormData((prev) => ({
        ...prev,
        [field]: response.location || response.file_name,
      }));
      toast.success("Imagem enviada com sucesso");
    } catch {
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploadingImage(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error("Nome é obrigatório");
      return;
    }

    if (!formData.description) {
      toast.error("Descrição Completa é obrigatória");
      return;
    }

    try {
      setLoading(true);

      // Auto-generate slug if empty
      const dataToSave = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name),
        meta_title: formData.meta_title || formData.name,
        meta_description: formData.meta_description || formData.description,
      };

      if (isEditing) {
        await influencerService.updateInfluencer(id!, dataToSave);
        toast.success("Influencer atualizado com sucesso!");
      } else {
        await influencerService.createInfluencer(dataToSave);
        toast.success("Influencer criado com sucesso!");
      }

      onBack();
    } catch (error: any) {
      console.error("Erro ao salvar influencer:", error);

      if (error.response?.status === 422) {
        const validationErrors = error.response.data.errors;
        const messages = Object.values(validationErrors).flat();
        toast.error(messages[0] as string || "Erro de validação nos dados fornecidos");
      } else {
        toast.error(isEditing ? "Erro ao atualizar influencer" : "Erro ao criar influencer");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (fetching) {
    return (
      <main className="flex-1 p-6 bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4C070]" />
      </main>
    );
  }

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Influencers
        </Button>
        <h1 className="text-2xl font-bold text-[#68686A]">
          {isEditing ? "Editar Influencer" : "Novo Influencer"}
        </h1>
        <p className="text-gray-600">
          {isEditing ? "Editar informações do parceiro influenciador" : "Cadastrar novo parceiro influenciador"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl space-y-6">
        {/* Imagens */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Foto da Influencer (Sugerido: 345px x 280px)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                {formData.cover ? (
                  <div className="space-y-2 mt-2">
                    <div className="relative">
                      <img
                        src={formData.cover}
                        alt="Preview"
                        className="w-full h-80 object-cover object-top rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, cover: "" }))}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 shadow-md"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center group hover:border-[#D4C070] transition-colors mt-2">
                    {uploadingImage === "cover" ? (
                      <Loader2 className="h-8 w-8 text-[#D4C070] mx-auto animate-spin" />
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2 group-hover:text-[#D4C070] transition-colors" />
                        <p className="text-sm text-[#68686A] mb-3">Foto da influencer (340px x 280px)</p>
                        <Label htmlFor="cover-upload" className="cursor-pointer inline-block bg-white border border-gray-300 px-4 py-2 rounded-md text-sm font-medium text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] transition-colors">
                          Selecionar Imagem
                        </Label>
                        <input
                          id="cover-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload("cover", file);
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Informações Básicas */}
        <Card>
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Nome <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                value={formData.name || ""}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Nome do influencer"
              />
            </div>

            <div>
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug || ""}
                onChange={(e) => updateField("slug", e.target.value)}
                placeholder="url-amigavel"
              />
              <p className="text-xs text-[#68686A] mt-1">Gerado automaticamente a partir do nome</p>
            </div>

            <div>
              <Label htmlFor="summary">Resumo / Summary</Label>
              <Textarea
                id="summary"
                value={formData.summary || ""}
                onChange={(e) => updateField("summary", e.target.value)}
                placeholder="Breve resumo exibido em listagens..."
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="description">Descrição Completa <span className="text-red-500">*</span></Label>
              <Textarea
                id="description"
                value={formData.description || ""}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="Descrição detalhada do influencer, estilo, público-alvo..."
                rows={4}
              />
            </div>

            <div className="flex items-center space-x-3">
              <button
                type="button"
                id="active"
                onClick={() => updateField("active", !(formData.active === true || Number(formData.active) === 1))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#D4C070] focus:ring-offset-2 ${formData.active === true || Number(formData.active) === 1 ? 'bg-[#849D70]' : 'bg-gray-300'
                  }`}
                title={formData.active === true || Number(formData.active) === 1 ? 'Clique para desativar' : 'Clique para ativar'}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.active === true || Number(formData.active) === 1 ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* SEO */}
        <Card>
          <CardHeader>
            <CardTitle>SEO / Metadados</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="meta_title">Meta Title</Label>
              <Input
                id="meta_title"
                value={formData.meta_title || ""}
                onChange={(e) => updateField("meta_title", e.target.value)}
                placeholder="Título para mecanismos de busca"
              />
              <p className="text-xs text-[#68686A] mt-1">
                {(formData.meta_title || formData.name || "").length}/60 caracteres recomendados
              </p>
            </div>

            <div>
              <Label htmlFor="meta_description">Meta Description</Label>
              <Textarea
                id="meta_description"
                value={formData.meta_description || ""}
                onChange={(e) => updateField("meta_description", e.target.value)}
                placeholder="Descrição para mecanismos de busca..."
                rows={2}
              />
              <p className="text-xs text-[#68686A] mt-1">
                {(formData.meta_description || formData.description || "").length}/160 caracteres recomendados
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4 pt-6">
          <Button type="submit" className="flex-1" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {isEditing ? "Salvar Alterações" : "Cadastrar Influencer"}
              </>
            )}
          </Button>
          <Button type="button" variant="outline" onClick={onBack} disabled={loading}>
            Cancelar
          </Button>
        </div>
      </form>
    </main>
  );
}

export default InfluencerForm;
